import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useDebounce } from 'use-debounce'
import { Box, Chip, IconButton, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SearchIcon from '@mui/icons-material/Search'
import { makeRequest } from '../../../apis/makeRequest'
import { getTransports } from '../../../apis/backendRequests'
import { notificationActions } from '../../../actions'
import { getApiErrorMessage } from '../../../helpers/logisticaHelper'
import useIncrementalList from '../../../hooks/useIncrementalList'
import {
    MainContainer,
    HeaderContainer,
    MainTitle,
    StyledTextField,
    StyledTable,
    StyledHeaderCell,
    StyledBodyCell,
    StyledBodyRow,
    EmptyState,
} from '../../../ui/StyledComponents'
import TransporteDialog from './TransporteDialog'

const PAGE_SIZE = 50
const COLUMNS = 7

const matchesSearch = (t, q) =>
    [t.code, t.description, t.address, t.city, ...t.plates.map((p) => p.plate)]
        .some((value) => value?.toLowerCase().includes(q))

const StatusChips = ({ transport }) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {transport.isRegistered
            ? <Chip label={transport.isActive ? 'Activo' : 'Inactivo'} size="small" color={transport.isActive ? 'success' : 'default'} />
            : <Chip label="Sin datos web" size="small" color="info" variant="outlined" />}
        {!transport.isInGp && <Chip label="Ya no existe en GP" size="small" color="warning" />}
    </Box>
)

export default function Transportes() {
    const dispatch = useDispatch()
    const [transports, setTransports] = useState([])
    const [loaded, setLoaded] = useState(false)
    // Código del transporte abierto en el diálogo
    const [editingCode, setEditingCode] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch] = useDebounce(searchTerm, 250)

    // Todas las formas de envío de GP, tengan o no datos web
    const cargarTransportes = useCallback(() => {
        makeRequest(
            () => getTransports({ includeInactive: true, includeUnregistered: true }),
            (res) => {
                setTransports(res.data)
                setLoaded(true)
            },
            (err) => dispatch(notificationActions.showError({ message: getApiErrorMessage(err) })),
            { checkEmptyResults: false }
        )
    }, [dispatch])

    useEffect(() => {
        cargarTransportes()
    }, [cargarTransportes])

    const filtered = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase()
        return q ? transports.filter((t) => matchesSearch(t, q)) : transports
    }, [transports, debouncedSearch])

    const isFilterPending = searchTerm.trim() !== debouncedSearch.trim()
    const { visibleItems, total, hasMore, scrollContainerRef, sentinelRef } = useIncrementalList(filtered, PAGE_SIZE, debouncedSearch)

    const editing = transports.find((t) => t.code === editingCode) ?? null

    // Datos y patentes devuelven el TransportDTO completo: reemplaza la fila
    const handleSaved = (transport) => {
        setTransports((prev) => prev.map((t) => (t.code === transport.code ? transport : t)))
    }

    // 500: el transporte ya no existe ni en GP ni en la web
    const handleTransportNotFound = () => {
        setEditingCode(null)
        cargarTransportes()
    }

    return (
        <MainContainer>
            <HeaderContainer>
                <MainTitle>Transportes Patentes</MainTitle>
            </HeaderContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
                <StyledTextField
                    size="small"
                    placeholder="Buscar por código, descripción, dirección o patente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 420 } }}
                />
                {searchTerm && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {isFilterPending ? 'Filtrando…' : `${total} resultado${total !== 1 ? 's' : ''}`}
                    </Typography>
                )}
            </Box>

            <TableContainer ref={scrollContainerRef} sx={{ maxHeight: { xs: 360, sm: 480, md: '65vh' }, overflow: 'auto' }}>
                <StyledTable size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledHeaderCell align="center">Código</StyledHeaderCell>
                            <StyledHeaderCell align="center">Descripción (GP)</StyledHeaderCell>
                            <StyledHeaderCell align="center">Tipo</StyledHeaderCell>
                            <StyledHeaderCell align="center">Dirección</StyledHeaderCell>
                            <StyledHeaderCell align="center">Patentes</StyledHeaderCell>
                            <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                            <StyledHeaderCell align="center">Editar</StyledHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleItems.length > 0 ? (
                            visibleItems.map((t) => (
                                <StyledBodyRow key={t.code}>
                                    <StyledBodyCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                                        {t.code}
                                        {t.isRetiraCliente && <Chip label="Retira cliente" size="small" sx={{ ml: 1 }} />}
                                    </StyledBodyCell>
                                    <StyledBodyCell align="left">{t.description}</StyledBodyCell>
                                    <StyledBodyCell align="center">{t.shippingTypeName}</StyledBodyCell>
                                    <StyledBodyCell align="left">{[t.address, t.city].filter(Boolean).join(', ')}</StyledBodyCell>
                                    <StyledBodyCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                                            {t.plates.map((p) => <Chip key={p.plate} label={p.plate} size="small" variant="outlined" />)}
                                        </Box>
                                    </StyledBodyCell>
                                    <StyledBodyCell align="center">
                                        <StatusChips transport={t} />
                                    </StyledBodyCell>
                                    <StyledBodyCell align="center">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => setEditingCode(t.code)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledBodyCell>
                                </StyledBodyRow>
                            ))
                        ) : (
                            <TableRow>
                                <StyledBodyCell colSpan={COLUMNS}>
                                    <EmptyState>
                                        {debouncedSearch.trim()
                                            ? `Sin resultados para «${debouncedSearch.trim()}».`
                                            : loaded ? 'No hay transportes cargados.' : 'Cargando transportes…'}
                                    </EmptyState>
                                </StyledBodyCell>
                            </TableRow>
                        )}
                    </TableBody>
                </StyledTable>
                {hasMore && <Box ref={sentinelRef} aria-hidden sx={{ height: 12, width: '100%' }} />}
            </TableContainer>
            {total > 0 && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, ml: 0.5, mb: 0 }}>
                    Mostrando {visibleItems.length} de {total}
                    {hasMore && ' — deslizá hacia abajo para cargar más'}
                </Typography>
            )}

            <TransporteDialog
                open={!!editing}
                transport={editing}
                onClose={() => setEditingCode(null)}
                onSaved={handleSaved}
                onTransportNotFound={handleTransportNotFound}
            />
        </MainContainer>
    )
}
